import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import cn from "classnames";
import { Description } from "@mui/icons-material";

import { useDocsFilenames, useUploadDocs } from "../../api/documentsApi";
import { MultiFileUpload } from "../../components/FileUpload";
import { useStudent, useStudentDocs } from "../../api/studentApi";
import { FileStatusDisplay } from "../../utils/fileStatus";
import { serverUrl, getAuthHeader } from "../../api";
import "./docsUpload.scss";

const DocsUpload = () => {
  const { data: fileList } = useDocsFilenames();
  const { data: student } = useStudent();
  const [directory, setDirectory] = useState<string>("");
  const { mutateAsync: uploadDocs } = useUploadDocs({ directory });
  const { data: docs, isLoading: isDocsLoading } = useStudentDocs({
    filter: { directory },
    enabled: !!directory,
  });
  const [status, setStatus] = useState<Record<string, Record<string, string>>>(
    {}
  );

  const [files, setFiles] = useState<Record<string, File[]>>({});

  useEffect(() => {
    if (docs) {
      const newStatus: Record<string, Record<string, string>> = {};
      Object.entries(docs.docs).forEach(([file, doc]: [string, any]) => {
        const directory = file.split("/")[0];
        const filename = file.split("/")[1];

        if (!newStatus[directory]) {
          newStatus[directory] = {};
        }

        newStatus[directory][filename] = doc.status || "uploaded";
      });
      setStatus(newStatus);
    }
  }, [docs]);

  const saveFiles = useCallback((filename: string, newFiles: File[]) => {
    setFiles((prev) => ({ ...prev, [filename]: newFiles }));
  }, []);

  const removeFiles = useCallback((filename: string) => {
    setFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[filename];
      return newFiles;
    });
  }, []);

  const upload = useCallback(
    async (filename: string) => {
      const originStatus = status[directory]?.[filename];
      setStatus((prev) => ({
        ...prev,
        [directory]: { ...prev[directory], [filename]: "uploading" },
      }));
      try {
        await uploadDocs({ filename, files: files[filename] });
        setStatus((prev) => ({
          ...prev,
          [directory]: { ...prev[directory], [filename]: "uploaded" },
        }));
      } catch (e: any) {
        setStatus((prev) => ({
          ...prev,
          [directory]: { ...prev[directory], [filename]: originStatus },
        }));
        if (e.response.data.detail === "D0401") {
          alert("이미지나 PDF 파일만 업로드 가능합니다.");
        } else {
          alert("업로드에 실패했습니다.");
        }
      }
      removeFiles(filename);
    },
    [files, directory, uploadDocs, removeFiles, status]
  );

  const getFile = useCallback(async (link: string) => {
    const response = await axios.get(
      `${serverUrl}/documents/signed_url/${link}`,
      {
        ...getAuthHeader(),
      }
    );
    const { data } = response;
    const url = data.url;

    const linkElement = document.createElement("a");
    linkElement.href = url;
    linkElement.download = link.split("/").pop() || "file";
    document.body.appendChild(linkElement);
    linkElement.click();
    URL.revokeObjectURL(url);
    linkElement.parentNode?.removeChild(linkElement);
  }, []);

  return (
    <div className="narrowContent paddingContent">
      <div className="horizontal mb16">
        {fileList &&
          Object.keys(fileList).map((dir) => (
            <button
              key={dir}
              onClick={() => setDirectory(dir)}
              className={cn({ selected: directory === dir })}
            >
              {dir}
            </button>
          ))}
      </div>
      {directory ? (
        isDocsLoading ? (
          <p>로딩 중...</p>
        ) : (
          <>
            <p
              style={{
                fontSize: "0.9em",
                color: "var(--gray-400)",
                marginBottom: "10px",
              }}
            >
              수락: 🟢 / 거절: 🔴 / 채점 안 됨: ⚪️
              <br />
              이미지(JPG/PNG)와 PDF만 업로드 가능합니다.
            </p>
            {fileList[directory].map((filename: string) => {
              return (
                <>
                  <p className="docs-title">
                    📂{"  "}
                    {filename}{" "}
                    {FileStatusDisplay[status?.[directory]?.[filename]]}
                  </p>
                  {docs.docs[`${directory}/${filename}`]?.link && (
                    <p className="uploaded-docs">
                      업로드한 파일 확인:{" "}
                      {docs.docs[`${directory}/${filename}`].link.map(
                        (link: string, idx: number) => {
                          return (
                            <span
                              className="docs-button"
                              onClick={() => getFile(link)}
                            >
                              <Description />
                              {idx + 1}
                            </span>
                          );
                        }
                      )}
                    </p>
                  )}
                  {status[directory]?.[filename] === "rejected" && (
                    <p key={`reject${filename}`} style={{ fontSize: "1.1em" }}>
                      거절 사유: {docs.docs[`${directory}/${filename}`].comment}
                    </p>
                  )}
                  <MultiFileUpload
                    key={filename}
                    filename={filename}
                    files={files[filename]}
                    setFiles={(newFiles: File[]) =>
                      saveFiles(filename, newFiles)
                    }
                    upload={async () => {
                      await upload(filename);
                    }}
                    strictFileType
                  />
                </>
              );
            })}
          </>
        )
      ) : (
        <>
          <p>업로드할 자료의 타입을 선택해주세요</p>
          {student?.student && !student.student.training_type && (
            <p style={{ color: "var(--primary-500)", fontWeight: "700" }}>
              <br />
              현장실습 관련 서류는 "현장실습 정보입력" 페이지에서
              <br />
              현장실습 타입 입력 후 업로드 가능합니다.
            </p>
          )}
        </>
      )}
      <div></div>
    </div>
  );
};

export default DocsUpload;
